import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Navigation, Save, Bell } from "lucide-react";
import { toast } from "sonner";

const TOGGLES = [
  { key: "gps_required", label: "GPS obrigatório", desc: "Bloqueia registo de ponto sem GPS ativo" },
  { key: "manual_entry_allowed", label: "Permitir entrada manual", desc: "Admin pode adicionar registros manualmente" },
  { key: "outside_location_requires_reason", label: "Exigir justificativa fora do local", desc: "Solicita motivo quando GPS indica fora do raio" },
];

const DEFAULT_CONFIG = {
  name: "", address: "",
  lat: "", lng: "",
  allowed_radius_meters: 50,
  gps_required: true,
  manual_entry_allowed: true,
  outside_location_requires_reason: false,
  notify_whatsapp: true,
  notify_email: false,
  admin_phone: "",
  admin_email: "",
};

export function ConfiguracaoTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [gettingLoc, setGettingLoc] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/timeclock/location-config");
      setConfig(prev => ({
        ...prev,
        ...res.data,
        lat: res.data.lat ?? "",
        lng: res.data.lng ?? "",
      }));
    } catch {}
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const set = (key, value) => setConfig(c => ({ ...c, [key]: value }));

  const handleGetLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocalização não disponível"); return; }
    setGettingLoc(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set("lat", pos.coords.latitude.toFixed(7));
        set("lng", pos.coords.longitude.toFixed(7));
        setGettingLoc(false);
        toast.success(`Localização obtida (precisão: ±${Math.round(pos.coords.accuracy)}m)`);
      },
      () => { setGettingLoc(false); toast.error("Não foi possível obter localização"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxiosRef.current.put("/timeclock/location-config", {
        ...config,
        lat: config.lat !== "" ? parseFloat(config.lat) : null,
        lng: config.lng !== "" ? parseFloat(config.lng) : null,
        allowed_radius_meters: parseInt(config.allowed_radius_meters) || 50,
      });
      toast.success("Configuração guardada!");
    } catch { toast.error("Erro ao guardar configuração"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-xl" data-testid="configuracao-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="w-4 h-4 text-emerald-600" />
            Local de Trabalho
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Nome do local</Label>
            <Input value={config.name} onChange={e => set("name", e.target.value)} placeholder="Ex: Restaurante Principal" />
          </div>
          <div>
            <Label className="text-xs">Endereço</Label>
            <Input value={config.address} onChange={e => set("address", e.target.value)} placeholder="Rua, número, cidade" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Latitude</Label>
              <Input
                value={config.lat}
                onChange={e => set("lat", e.target.value)}
                placeholder="37.3891"
                className="font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Longitude</Label>
              <Input
                value={config.lng}
                onChange={e => set("lng", e.target.value)}
                placeholder="-5.9845"
                className="font-mono text-sm"
              />
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={handleGetLocation}
            disabled={gettingLoc}
          >
            <Navigation className="w-4 h-4" />
            {gettingLoc ? "A obter localização..." : "Usar localização atual do dispositivo"}
          </Button>

          <div>
            <Label className="text-xs">
              Raio permitido — <span className="font-bold text-foreground">{config.allowed_radius_meters}m</span>
            </Label>
            <div className="flex items-center gap-3 mt-1">
              <input
                type="range" min="10" max="500" step="10"
                value={config.allowed_radius_meters}
                onChange={e => set("allowed_radius_meters", parseInt(e.target.value))}
                className="flex-1 accent-emerald-600"
              />
              <Input
                type="number" min="10" max="500" step="10"
                value={config.allowed_radius_meters}
                onChange={e => set("allowed_radius_meters", parseInt(e.target.value) || 50)}
                className="w-20 text-center font-bold"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Funcionário deve estar neste raio para registar ponto. Padrão: 50m.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Regras de Registo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {TOGGLES.map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
              <Switch
                checked={!!config[key]}
                onCheckedChange={v => set(key, v)}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="w-4 h-4 text-emerald-600" />
            Notificações
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Notificar por WhatsApp</p>
              <p className="text-xs text-muted-foreground">Envia alertas e lembretes via WhatsApp</p>
            </div>
            <Switch checked={!!config.notify_whatsapp} onCheckedChange={v => set("notify_whatsapp", v)} />
          </div>
          {config.notify_whatsapp && (
            <div>
              <Label className="text-xs">Telefone do admin para alertas</Label>
              <Input
                value={config.admin_phone}
                onChange={e => set("admin_phone", e.target.value)}
                placeholder="+351912345678"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Inclua o código do país (ex: +351)</p>
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Notificar por e-mail</p>
              <p className="text-xs text-muted-foreground">Fallback para e-mail se WhatsApp falhar</p>
            </div>
            <Switch checked={!!config.notify_email} onCheckedChange={v => set("notify_email", v)} />
          </div>
          {config.notify_email && (
            <div>
              <Label className="text-xs">E-mail do admin para fallback</Label>
              <Input
                value={config.admin_email}
                onChange={e => set("admin_email", e.target.value)}
                placeholder="admin@restaurante.com"
                type="email"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Button
        className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
        onClick={handleSave}
        disabled={saving}
      >
        <Save className="w-4 h-4" />
        {saving ? "A guardar..." : "Guardar Configuração"}
      </Button>
    </div>
  );
}
